import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Logo } from './Logo';
import { Flame, Icon, Star } from './Icons';
import { useAuth } from '@/auth/AuthProvider';
import { api } from '@/lib/api';
import { fmt, days } from '@/lib/format';

const STUDENT_NAV = [
  { to: '/app', label: 'Головна', icon: 'home', end: true },
  { to: '/app/path', label: 'Навчання', icon: 'map' },
  { to: '/app/progress', label: 'Прогрес', icon: 'chart' },
  { to: '/app/achievements', label: 'Досягнення', icon: 'award' },
];

export function useStudentSummary() {
  return useQuery({ queryKey: ['summary', 'me'], queryFn: () => api.childSummary() });
}

function SignOut({ compact = false }: { compact?: boolean }) {
  const { signOut } = useAuth();
  const nav = useNavigate();
  return (
    <button className={compact ? 'icon-btn' : 'flex items-center gap-3 rounded-2xl px-3.5 py-3 font-bold text-ink-muted hover:bg-surface-2'}
      onClick={async () => { await signOut(); nav('/login'); }} aria-label="Вийти">
      <Icon name="logout" />{!compact && 'Вийти'}
    </button>
  );
}

export function StatChips() {
  const { data } = useStudentSummary();
  if (!data) return null;
  return (
    <div className="flex gap-2.5">
      <span className="chip" title="Днів поспіль"><Flame /><b>{data.streak}</b><small className="hidden text-xs font-bold text-ink-muted sm:inline">{days(data.streak).replace(/^\d+ /, '')} поспіль</small></span>
      <span className="chip"><Star /><b>{fmt(data.xpTotal)} XP</b></span>
    </div>
  );
}

export function StudentShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[236px_minmax(0,1fr)]">
      <aside className="sticky top-0 hidden h-dvh flex-col gap-7 border-r border-line bg-white px-3.5 py-6 lg:flex">
        <div className="pl-3.5"><Logo to="/app" /></div>
        <nav className="flex flex-col gap-1" aria-label="Основна навігація">
          {STUDENT_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) =>
              `flex items-center gap-3 rounded-2xl px-3.5 py-3 font-bold ${isActive ? 'bg-brand-soft text-brand' : 'text-ink-muted hover:bg-surface-2'}`}>
              <Icon name={n.icon} />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto"><SignOut /></div>
      </aside>
      <div className="min-w-0">
        <header className="sticky top-0 z-10 flex items-center gap-3 bg-page px-4 py-3 lg:hidden">
          <div className="mr-auto"><Logo to="/app" small /></div>
          <StatChips /><SignOut compact />
        </header>
        <main className="mx-auto w-full max-w-[1100px] px-4 pb-28 pt-2 lg:px-9 lg:pb-12 lg:pt-8">{children}</main>
        <nav className="fixed inset-x-0 bottom-0 z-20 flex justify-around border-t border-line bg-white px-1.5 pt-2 lg:hidden"
          style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' }} aria-label="Основна навігація">
          {STUDENT_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1 text-[11px] font-extrabold ${isActive ? 'text-brand' : 'text-ink-muted'}`}>
              <Icon name={n.icon} size={22} />{n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

export function ParentShell({ children }: { children: ReactNode }) {
  const { isAdmin, isEditor, profile } = useAuth();
  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-10 border-b border-line bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1100px] items-center gap-3 px-4 py-3 lg:px-8">
          <div className="mr-auto"><Logo to="/parent/children" small /></div>
          <NavLink to="/parent/children" className={({ isActive }) => `rounded-xl px-3 py-2 font-bold ${isActive ? 'bg-brand-soft text-brand' : 'text-ink-muted'}`}>Мої діти</NavLink>
          {isEditor && <NavLink to="/studio" className={({ isActive }) => `rounded-xl px-3 py-2 font-bold ${isActive ? 'bg-brand-soft text-brand' : 'text-ink-muted'}`}>Кабінет викладача</NavLink>}
          {isAdmin && <NavLink to="/admin" className={({ isActive }) => `rounded-xl px-3 py-2 font-bold ${isActive ? 'bg-brand-soft text-brand' : 'text-ink-muted'}`}>Адмін</NavLink>}
          <span className="hidden font-semibold text-ink-muted sm:inline">{profile?.display_name}</span>
          <SignOut compact />
        </div>
      </header>
      <main className="mx-auto w-full max-w-[1100px] px-4 py-6 lg:px-8 lg:py-10">{children}</main>
    </div>
  );
}

export function PageHead({ title, sub, right }: { title: ReactNode; sub?: ReactNode; right?: ReactNode }) {
  return (
    <section className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div><h1 className="text-[clamp(26px,3.4vw,34px)] font-black leading-tight">{title}</h1>{sub && <p className="mt-1.5 font-semibold text-ink-muted">{sub}</p>}</div>
      {right}
    </section>
  );
}
